import React, { useReducer, useContext, Dispatch, Reducer, useEffect, useRef } from "react"

import { Product, ProductGroup } from "typings/products"
import { RootQueryUserFragment, JourneyKindEnum } from "typings/types"

import { useProductsState } from "shared/context/products"
import { maybeGetActiveJourney } from "shared/user/helpers"

import { useUpdateUserProducts } from "graphql/mutations/updateUserProducts"
import { useRootQuery } from "graphql/smart-queries/user"

import attempt from "lodash.attempt"
import { canAccessLocalStorage } from "./helpers"

export enum CartActionEventType {
  addToCart = "addToCart",
  removeFromCart = "removeFromCart",
  reinitializeCart = "reinitializeCart",
  batchRemoveFromCart = "batchRemoveFromCart",
  replaceInCart = "replaceInCart",
  clearCart = "clearCart"
}

interface AddToCart {
  event: typeof CartActionEventType.addToCart
  cartItem: Product
}

interface RemoveFromCart {
  event: typeof CartActionEventType.removeFromCart
  cartItem: Product
}

interface ReinitializeCart {
  /**
   * if the user is logged in then we want to update cart state with user's
   * selected items and set then "userLoggedIn" bool to true in cart state
   */
  event: typeof CartActionEventType.reinitializeCart
  cartItems: Product[]
}

interface BatchRemoveFromCart {
  event: typeof CartActionEventType.batchRemoveFromCart
  productIds: number[]
}

interface ReplaceInCart {
  event: typeof CartActionEventType.replaceInCart
  cartItem: Product
}

interface ClearCart {
  event: typeof CartActionEventType.clearCart
}

export type AnyAction = AddToCart | RemoveFromCart | ReinitializeCart | BatchRemoveFromCart | ReplaceInCart | ClearCart

interface State {
  cartItems: Product[]
  userLoggedIn: boolean
}

const initialState: State = {
  cartItems: [],
  userLoggedIn: false
}

const saveCart = (productIds: number[]) => {
  window.localStorage.setItem("cartItems", JSON.stringify(productIds))
}

const reducer: React.Reducer<State, AnyAction> = (state, action) => {
  let newProducts = []
  switch (action.event) {
    /* This event is not designed to be called from code - use useCartApi instead */
    case CartActionEventType.addToCart:
      state = {
        ...state,
        cartItems: [action.cartItem, ...state.cartItems]
      }
      break
    /* This event is not designed to be called from code - use useCartApi instead */
    case CartActionEventType.removeFromCart:
      state = {
        ...state,
        cartItems: state.cartItems.filter(item => item.id !== action.cartItem.id)
      }
      break
    case CartActionEventType.reinitializeCart:
      window?.localStorage.removeItem("cartItems")

      state = {
        ...state,
        cartItems: action.cartItems,
        userLoggedIn: true
      }
      break
    case CartActionEventType.batchRemoveFromCart:
      state = {
        ...state,
        cartItems: state.cartItems.filter(item => !action.productIds.includes(item.id))
      }
      break
    /* This event is not designed to be called from code - use useCartApi instead */
    case CartActionEventType.replaceInCart:
      newProducts = [
        ...state.cartItems.filter(
          item => item.drugType !== action.cartItem.drugType || item.dosageType !== action.cartItem.dosageType
        ),
        action.cartItem
      ]
      state = {
        ...state,
        cartItems: newProducts
      }
      break
    case CartActionEventType.clearCart:
      state = {
        ...state,
        cartItems: []
      }
      break
    default:
      state = state || initialState
  }

  if (!state.userLoggedIn && canAccessLocalStorage()) {
    saveCart(state.cartItems.map(p => p.id))
  }

  return state
}

const getInitialStateFromLocalStorage = (productGroups: ProductGroup[]): State => {
  const localData = canAccessLocalStorage() ? window.localStorage.getItem("cartItems") : null
  const parsedLocalData = parseLodash(localData)
  const previousCartItems = Array.isArray(parsedLocalData) ? parsedLocalData : []

  /* determine if old cart type and convert to new cart type of productIds if true */
  const oldCart: boolean = isOldCart(previousCartItems)
  const newCart: number[] = oldCart ? convertOldCart(previousCartItems) : previousCartItems

  /* map cart's productIds to full-blown product items */
  const cartProductItems: Product[] = productGroups.flatMap(pg => pg.products).filter(p => newCart.includes(p.id))

  return {
    ...initialState,
    cartItems: cartProductItems
  }
}

/* checking if cart is old if it is not a number array */
function isOldCart(cart: Product[] | number[]): boolean {
  return cart && cart.length > 0 && typeof cart[0] !== "number"
}

const parseLodash = (jsonStr: any) => attempt(JSON.parse.bind(null, jsonStr))

const convertOldCart = (cart: Product[]): number[] => cart.map(p => p.id)

const CartContext = React.createContext<{ state: State; dispatch: Dispatch<AnyAction> }>({
  state: initialState,
  dispatch: () => null
})

function CartProvider({ children }: { children?: React.ReactNode }) {
  const { productGroups } = useProductsState()
  const [state, dispatch] = useReducer<Reducer<State, AnyAction>>(
    reducer,
    getInitialStateFromLocalStorage(productGroups)
  )
  return <CartContext.Provider value={{ state, dispatch }}>{children}</CartContext.Provider>
}

export const useCartState = () => useContext(CartContext)

/**
 * called after useRootQuery from pages/index.tsx
 * if there is a user in an active journey (whether it's after a lead is created or they sign back in)
 * then we rehydrate cart state with UserSelectedProducts
 * @param currentUser
 */
export const useUpdateUserCartState = (currentUser: Maybe<RootQueryUserFragment>) => {
  const { dispatch } = useCartState()
  const { productGroups } = useProductsState()

  const userLoaded = useRef(false)
  useEffect(() => {
    if (!userLoaded.current && currentUser) {
      const journey = maybeGetActiveJourney(currentUser)
      if (!journey) {
        return
      }

      /* update cart state with current user and their UserSelectedProducts */
      const productIds = journey.userProducts.map(p => p.id)
      const products = productGroups.flatMap(pg => pg.products).filter(p => productIds.includes(p.id))
      dispatch({ event: CartActionEventType.reinitializeCart, cartItems: products })
      userLoaded.current = true
    }
  }, [currentUser, productGroups])
}

/**
 * called when we want to dispatch state and do a mutation
 * if there is a user *WITH* an active journey, as the
 * mutation stores UserSelectedProducts in the journey.
 */
export const useCartApi = () => {
  const { dispatch } = useCartState()

  const [updateUserProducts] = useUpdateUserProducts()
  const { data, loading } = useRootQuery()

  if (loading || !data) {
    return {
      onAddProduct: () => {},
      onReplaceProduct: () => {},
      onRemoveProduct: () => {},
      onClearCart: () => {},
      onBatchRemoveFromCart: () => {}
    }
  }

  const { user } = data

  return {
    onAddProduct: async (selectedProduct?: Product, journeyKind?: JourneyKindEnum) => {
      if (!selectedProduct) {
        return
      }
      dispatch({ event: CartActionEventType.addToCart, cartItem: selectedProduct })
      if (user) {
        const journey = maybeGetActiveJourney(user as RootQueryUserFragment)

        if (!journey && !journeyKind) {
          return
        }

        const previousProductIds = journey?.userProducts.map(p => p.id) || []

        const updateUserProductsResults = await updateUserProducts({
          variables: {
            params: {
              productIds: [...previousProductIds, selectedProduct.id],
              journeyKind: journeyKind || journey?.kind!
            }
          }
        })

        if (updateUserProductsResults.errors) {
          throw Error("Could not add user's selected product")
        }
      }
    },
    onReplaceProduct: async (selectedProduct?: Product) => {
      if (!selectedProduct) {
        return
      }
      dispatch({ event: CartActionEventType.replaceInCart, cartItem: selectedProduct })

      if (user) {
        const journey = maybeGetActiveJourney(user as RootQueryUserFragment)
        if (!journey) {
          return
        }
        const updateUserProductsResults = await updateUserProducts({
          variables: {
            params: {
              productIds: [
                ...journey.userProducts
                  .filter(
                    item => item.drugType !== selectedProduct.drugType || item.dosageType !== selectedProduct.dosageType
                  )
                  .map(p => p.id),
                selectedProduct.id
              ],
              journeyKind: journey.kind
            }
          }
        })
        if (updateUserProductsResults.errors) {
          throw Error("Could not replace user's selected product")
        }
      }
    },
    onRemoveProduct: async (selectedProduct?: Product) => {
      if (!selectedProduct) {
        return
      }
      dispatch({ event: CartActionEventType.removeFromCart, cartItem: selectedProduct })
      if (user) {
        const journey = maybeGetActiveJourney(user as RootQueryUserFragment)
        if (!journey) {
          return
        }
        const updateUserProductsResults = await updateUserProducts({
          variables: {
            params: {
              productIds: [...journey.userProducts.map(p => p.id).filter(id => id !== selectedProduct.id)],
              journeyKind: journey.kind
            }
          }
        })
        if (updateUserProductsResults.errors) {
          throw Error("Could not remove user's selected product")
        }
      }
    },
    onClearCart: async () => {
      dispatch({ event: CartActionEventType.clearCart })
      if (user) {
        const journey = maybeGetActiveJourney(user as RootQueryUserFragment)
        if (!journey) {
          return
        }
        const updateUserProductsResults = await updateUserProducts({
          variables: {
            params: {
              productIds: [],
              journeyKind: journey.kind
            }
          }
        })
        if (updateUserProductsResults.errors) {
          throw Error("Could not clear the user's cart")
        }
      }
    },
    onBatchRemoveFromCart: async (productIds: number[]) => {
      dispatch({ event: CartActionEventType.batchRemoveFromCart, productIds: productIds })
      if (user) {
        const journey = maybeGetActiveJourney(user as RootQueryUserFragment)
        if (!journey) {
          return
        }
        const updateUserProductsResults = await updateUserProducts({
          variables: {
            params: {
              productIds: [...journey.userProducts.map(p => p.id).filter(pid => !productIds.includes(pid))],
              journeyKind: journey.kind
            }
          }
        })
        if (updateUserProductsResults.errors) {
          throw Error("Could not remove products from the user's cart")
        }
      }
    }
  }
}

export { CartProvider }
